import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import './SportsNavSidebar.css';

interface SportsNavSidebarProps {
  selectedSubCategory: string;
  onSubCategoryChange: (subCategory: string) => void;
  hasLiveGames?: boolean;
}

interface SportCategory {
  name: string;
  displayName: string;
  subcategories?: string[];
  apiKey?: string;
}

export const SPORTS_CATEGORIES: SportCategory[] = [
  {
    name: 'NFL',
    displayName: 'NFL',
    apiKey: 'NFL'
  },
  {
    name: 'CFB',
    displayName: 'College Football',
    apiKey: 'CFB'
  },
  {
    name: 'Soccer',
    displayName: 'Soccer',
    subcategories: ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1', 'MLS', 'FIFA Friendlies', 'Champions League']
  },
  {
    name: 'MLB',
    displayName: 'MLB',
    apiKey: 'MLB'
  },
  {
    name: 'NBA',
    displayName: 'NBA',
    apiKey: 'NBA'
  },
  {
    name: 'PremierLeague',
    displayName: 'Premier League',
    apiKey: 'Premier League'
  },
  {
    name: 'Formula1',
    displayName: 'Formula 1',
    apiKey: 'Formula 1'
  },
  {
    name: 'NHL',
    displayName: 'NHL',
    apiKey: 'NHL'
  },
  {
    name: 'Tennis',
    displayName: 'Tennis',
    apiKey: 'Tennis'
  },
  {
    name: 'LaLiga',
    displayName: 'La Liga',
    apiKey: 'La Liga'
  },
  {
    name: 'Cricket',
    displayName: 'Cricket',
    apiKey: 'Cricket'
  },
  {
    name: 'Boxing',
    displayName: 'Boxing',
    apiKey: 'Boxing'
  },
  {
    name: 'UFC',
    displayName: 'UFC',
    apiKey: 'UFC'
  },
  {
    name: 'Ligue1',
    displayName: 'Ligue 1',
    apiKey: 'Ligue 1'
  },
  {
    name: 'MLS',
    displayName: 'MLS',
    apiKey: 'MLS'
  },
  {
    name: 'Golf',
    displayName: 'Golf',
    apiKey: 'Golf'
  },
  {
    name: 'ChampionsLeague',
    displayName: 'Champions League',
    apiKey: 'Champions League'
  },
  {
    name: 'FIFAFriendlies',
    displayName: 'FIFA Friendlies',
    apiKey: 'FIFA Friendlies'
  },
  {
    name: 'Chess',
    displayName: 'Chess',
    apiKey: 'Chess'
  }
];

const SportsNavSidebar: React.FC<SportsNavSidebarProps> = ({
  selectedSubCategory,
  onSubCategoryChange,
  hasLiveGames = false
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set() // No categories expanded by default
  );

  const toggleCategory = (categoryName: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryName)) {
      newExpanded.delete(categoryName);
    } else {
      newExpanded.add(categoryName);
    }
    setExpandedCategories(newExpanded);
  };

  const isActive = (subCategory: string) => {
    return selectedSubCategory === subCategory;
  };

  const isCategoryActive = (category: SportCategory) => {
    if (category.subcategories) {
      // Don't show parent category as active when a subcategory is selected
      return false;
    }
    return category.apiKey === selectedSubCategory;
  };

  const handleCategoryClick = (category: SportCategory) => {
    if (category.subcategories) {
      toggleCategory(category.name);
    } else if (category.apiKey) {
      onSubCategoryChange(category.apiKey);
      try {
        posthog.capture('engagement:secondary_category_selected', {
          category: 'Sports',
          sub_category: category.apiKey,
        });
      } catch {}
    }
  };

  // Auto-expand parent categories when subcategory is selected
  useEffect(() => {
    if (selectedSubCategory) {
      const parentCategory = SPORTS_CATEGORIES.find(category => 
        category.subcategories && category.subcategories.includes(selectedSubCategory)
      );
      if (parentCategory) {
        setExpandedCategories(prev => {
          const newSet = new Set(prev);
          newSet.add(parentCategory.name);
          return newSet;
        });
      }
    }
  }, [selectedSubCategory]);

  return (
    <div className="sports-sidebar">
      {/* Live option - only show if there are live games */}
      {hasLiveGames && (
        <div
          className={`sports-sidebar-item ${selectedSubCategory === 'Live' ? 'active' : ''}`}
          onClick={() => {
            onSubCategoryChange('Live');
            try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: 'Live' }); } catch {}
          }}
        >
          <span className="sports-label">LIVE</span>
        </div>
      )}

      {/* All option */}
      <div
        className={`sports-sidebar-item ${selectedSubCategory === '' ? 'active' : ''}`}
        onClick={() => {
          onSubCategoryChange('');
          try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: 'All' }); } catch {}
        }}
      >
        <span className="sports-label">All</span>
      </div>

      {/* Sport categories */}
      {SPORTS_CATEGORIES.filter(category =>
        // Hide standalone Premier League, La Liga, Ligue 1, MLS, Champions League, and FIFA Friendlies on desktop - they show under Soccer subcategories
        category.name !== 'PremierLeague' && category.name !== 'LaLiga' && category.name !== 'Ligue1' && category.name !== 'MLS' && category.name !== 'ChampionsLeague' && category.name !== 'FIFAFriendlies'
      ).map((category) => (
        <div key={category.name} className="sports-sidebar-category">
          <div 
            className={`sports-sidebar-item sports-sidebar-section ${isCategoryActive(category) ? 'active' : ''}`}
            onClick={() => handleCategoryClick(category)}
          >
            <span className="sports-label">{category.displayName}</span>
            {category.subcategories && (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="12" 
                height="12" 
                fill="currentColor" 
                viewBox="0 0 16 16"
                className={`chevron ${expandedCategories.has(category.name) ? 'expanded' : ''}`}
              >
                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"/>
              </svg>
            )}
          </div>

          {/* Subcategories */}
          {category.subcategories && expandedCategories.has(category.name) && (
            <div className="sports-sidebar-subcategories">
              {category.subcategories.map((sub) => (
                <div
                  key={sub}
                  className={`sports-sidebar-item sports-sidebar-subcategory ${isActive(sub) ? 'active' : ''}`}
                  onClick={() => {
                    onSubCategoryChange(sub);
                    try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: sub }); } catch {}
                  }}
                >
                  {sub}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SportsNavSidebar;

